export const serviceKey = (item) => item?.serviceId || item?.service_id || item?.id || item?.serviceCode || item?.service_code || item?.code;
export const serviceCode = (item) => item?.serviceCode || item?.service_code || item?.code || "";
export const isParkingOffer = (item) => /(^|-)PARK$/i.test(serviceCode(item) || String(item?.id || ""));
export const isTaxiOffer = (item) => /(^|-)(AIRTX|PSFTX)$/i.test(serviceCode(item)) || /taxi/i.test(String(item?.name || ""));
export const isLateCheckoutOffer = (item) => /(^|-)LCO$/i.test(serviceCode(item) || String(serviceKey(item) || ""));
export const isSaunaOffer = (item) => {
  const code = serviceCode(item) || String(serviceKey(item) || "");
  return /(^|-)(SAUNA|SAUCO)$/i.test(code) || /sauna/i.test(String(item?.name || ""));
};
export const isIncludedSaunaOffer = (item) => /(^|-)SAUCO$/i.test(serviceCode(item) || String(serviceKey(item) || ""));
const isPsfSaunaOffer = (item) => /(^|-)PSF$/i.test(serviceCode(item) || String(serviceKey(item) || "")) &&
  /sauna/i.test(String(item?.name || ""));
export const isIncludedOffer = (item) => item?.includedInPackage === true || item?.included_in_package === true || item?.isIncluded === true || item?.is_included === true;
export const canRemoveService = (item) => item?.canRemove === true || item?.can_remove === true;
export const breakfastCode = (item) => String(serviceCode(item) || serviceKey(item) || "").toUpperCase().split("-").pop();
export const isBreakfastService = (item) => {
  const code = breakfastCode(item);
  return ["FRST", "FRSTKI", "PSF"].includes(code) ||
    String(item?.packageServiceGroup || item?.package_service_group || "").toLowerCase() === "breakfast" ||
    /breakfast|fr[üu]hst[üu]ck|fruehstueck/i.test(String(item?.name || ""));
};
export const canBookOffer = (offer) => (offer?.canBook === true || offer?.can_book === true) &&
  offer?.canAdd !== false && offer?.can_add !== false;

export const breakfastWizardOffers = (offers, selectedServices) => {
  const selected = selectedServices.find(isBreakfastService);
  const adult = offers.find((offer) => breakfastCode(offer) === "FRST");
  const child = offers.find((offer) => breakfastCode(offer) === "FRSTKI");
  const selectedOffer = selected && (offers.find((offer) => selectedForOffer(offer, [selected]).length) || selected);
  const visibleBreakfast = selected
    ? breakfastCode(selected) === "PSF" ? selectedOffer : adult || (breakfastCode(selected) === "FRSTKI" ? null : selectedOffer)
    : adult || (!child ? offers.find(isBreakfastService) : null);
  let shownBreakfast = false;
  const pages = offers.flatMap((offer) => {
    if (!isBreakfastService(offer)) return [offer];
    if (shownBreakfast || !visibleBreakfast) return [];
    shownBreakfast = true;
    return [selected ? visibleBreakfast : { ...visibleBreakfast, childBreakfastOffer: child }];
  });
  if (selected && !shownBreakfast && visibleBreakfast) pages.unshift(selectedOffer);
  return pages;
};

export const normalizeIncludedSauna = (offers = [], selectedServices = []) => {
  const sauna = offers.find(isIncludedSaunaOffer);
  const visibleSelectedServices = selectedServices.filter((service) =>
    !isIncludedSaunaOffer(service) && !isPsfSaunaOffer(service));
  if (!sauna) return { offers, selectedServices: visibleSelectedServices };

  const includedSauna = {
    ...sauna,
    serviceCode: "SAUCO",
    code: "SAUCO",
    includedInPackage: true,
    isIncluded: true,
    canBook: false,
    canAdd: false,
    canModify: false,
    canRemove: false,
    displayUnitPrice: { amount: 0, currency: "EUR" },
    totalAmount: { amount: 0, currency: "EUR" },
  };
  let saunaAdded = false;
  const normalizedOffers = offers.flatMap((offer) => {
    if (!isIncludedSaunaOffer(offer) && !isPsfSaunaOffer(offer)) return [offer];
    if (saunaAdded) return [];
    saunaAdded = true;
    return [includedSauna];
  });

  return {
    offers: normalizedOffers,
    selectedServices: visibleSelectedServices,
  };
};

const positiveInteger = (value) => Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : undefined;
export const reservationGuestLimit = (reservation) => {
  const candidates = [reservation?.occupancy, reservation?.room, reservation?.stay, reservation,
    ...(Array.isArray(reservation?.rooms) && reservation.rooms.length === 1 ? reservation.rooms : [])];
  for (const item of candidates) {
    const direct = positiveInteger(item?.guestCount ?? item?.guest_count ?? item?.numberOfGuests ?? item?.number_of_guests ?? item?.personCount ?? item?.person_count ?? item?.totalGuests ?? item?.total_guests);
    if (direct) return direct;
    const adults = positiveInteger(item?.adults ?? item?.adultCount ?? item?.adult_count);
    const children = positiveInteger(item?.children ?? item?.childCount ?? item?.child_count);
    if (adults || children) return (adults || 0) + (children || 0);
  }
  if (Array.isArray(reservation?.guests) && reservation.guests.length) return reservation.guests.length;
  return undefined;
};
export const serviceIdentity = (item) => item.serviceId || item.service_id || item.id
  ? { serviceId: serviceKey(item) } : { serviceCode: serviceCode(item) };

export const selectedForOffer = (offer, selectedServices) => {
  const id = String(offer?.serviceId || offer?.service_id || offer?.id || "");
  const code = String(serviceCode(offer)).toUpperCase();
  return selectedServices.filter((item) =>
    (id && String(item.serviceId || item.service_id || item.id || "") === id) ||
    (code && String(serviceCode(item)).toUpperCase() === code));
};

export const serviceDates = (offer) => (offer.dates || [])
  .map((item) => typeof item === "string" ? item : item.serviceDate || item.date)
  .filter(Boolean);

// These services cover the stay in the existing kiosk flow.
export const requiresWholeStay = (offer) => offer.requiresWholeStay === true || offer.requires_whole_stay === true ||
  /(^|-)(FRST|FRSTKI|PARK|BBB|HAUS)$/i.test(serviceCode(offer) || String(serviceKey(offer) || ""));

export const serviceQuantityLimit = (offer) => {
  if (String(offer.pricingUnit || offer.pricing_unit).toLowerCase() === "room") return 1;
  const available = offer.availableCount ?? offer.available_count;
  const limit = Number(available);
  return available !== undefined && available !== null && Number.isInteger(limit) && limit >= 0 ? limit : undefined;
};

export const breakfastSelectionError = (offer, details, guestLimit) => {
  const adult = Number(details.count);
  const child = Number(details.childBreakfastCount || 0);
  const free = Number(details.freeKidBreakfastCount || 0);
  const adultError = serviceSelectionError(offer, details);
  if (adultError) return adultError;
  if (![child, free].every((count) => Number.isInteger(count) && count >= 0)) return "services.quantityRequired";
  if (guestLimit !== undefined && adult + child + free > guestLimit) return "services.breakfastGuestLimit";
  if (child > 0) {
    const childOffer = offer.childBreakfastOffer;
    if (!childOffer || !canBookOffer(childOffer) || child > (serviceQuantityLimit(childOffer) ?? Infinity) ||
      !serviceSelectionPrice(childOffer, { count: child, wholeStay: true })) {
      return "services.breakfastChildUnavailable";
    }
  }
  return "";
};

export const defaultServiceDetails = (offer) => {
  const count = Number(offer.count);
  return {
    count: Math.min(Number.isInteger(count) && count > 0 ? count : 1, serviceQuantityLimit(offer) ?? Infinity),
    wholeStay: !isSaunaOffer(offer) && requiresWholeStay(offer),
    dates: isTaxiOffer(offer) || isLateCheckoutOffer(offer) || (!isSaunaOffer(offer) && requiresWholeStay(offer)) ? [] : serviceDates(offer).slice(0, 1),
    taxiPickupDate: offer.defaultTaxiPickupDate || offer.default_taxi_pickup_date || "",
    taxiPickupTime: "",
    taxiPickupNote: "",
    licensePlate: "",
  };
};

export const serviceSelectionError = (offer, details) => {
  const count = Number(details.count);
  if (!Number.isInteger(count) || count < 1 || count > (serviceQuantityLimit(offer) ?? Infinity)) return "services.quantityRequired";
  if (isParkingOffer(offer) && !details.licensePlate?.trim()) return "services.licensePlateRequired";
  if (isTaxiOffer(offer)) {
    const pickupDates = offer.taxiPickupDates || offer.taxi_pickup_dates || [];
    if (!details.taxiPickupDate || !details.taxiPickupTime || (pickupDates.length && !pickupDates.includes(details.taxiPickupDate))) return "services.taxiRequired";
    if (!/^(?:[01]\d|2[0-3]):(?:00|15|30|45)$/.test(details.taxiPickupTime)) return "services.taxiQuarterHour";
  } else if (!isLateCheckoutOffer(offer) && serviceDates(offer).length && (!details.wholeStay || isSaunaOffer(offer)) &&
    (!details.dates?.length || details.dates.some((date) => !serviceDates(offer).includes(date)))) {
    return "services.datesRequired";
  }
  return "";
};

export const serviceBookPayload = (offer, details) => ({
  ...serviceIdentity(offer),
  ...(serviceCode(offer) ? { serviceCode: serviceCode(offer) } : {}),
  count: Number(details.count),
  ...(isParkingOffer(offer) ? { licensePlate: details.licensePlate.trim() } : {}),
  ...(isLateCheckoutOffer(offer) ? {} : isSaunaOffer(offer) ? details.dates?.length ? { dates: details.dates } : {} : isTaxiOffer(offer) ? {
    taxiPickupDate: details.taxiPickupDate,
    taxiPickupTime: details.taxiPickupTime,
    taxiPickupNote: details.taxiPickupNote || "",
  } : details.wholeStay ? { wholeStay: true } : details.dates?.length ? { dates: details.dates } : {}),
});

const asMoney = (value) => {
  const amount = value?.amount ?? value?.grossAmount;
  return amount !== undefined && amount !== null && amount !== "" && Number.isFinite(Number(amount)) && value.currency
    ? { amount: Number(amount), currency: value.currency } : null;
};

const sumMoney = (values) => {
  if (!values.length || values.some((value) => !value || value.currency !== values[0]?.currency)) return null;
  return { amount: Math.round(values.reduce((sum, value) => sum + value.amount, 0) * 100) / 100, currency: values[0].currency };
};

export const serviceSelectionPrice = (offer, details) => {
  const dates = isTaxiOffer(offer) || isLateCheckoutOffer(offer) ? [] : details.wholeStay && !isSaunaOffer(offer) ? serviceDates(offer) : details.dates || [];
  const base = dates.length ? sumMoney(dates.map((date) => {
    const dateOffer = (offer.dates || []).find((item) => (item.serviceDate || item.date) === date);
    return asMoney(dateOffer?.amount);
  })) : asMoney(offer.totalAmount || offer.total_amount || offer.price || offer.displayUnitPrice || offer.display_unit_price);
  const count = Number(details.count);
  if (!base || !Number.isInteger(count) || count < 1) return null;
  return { ...base, amount: Math.round(base.amount * count * 100) / 100 };
};

export const breakfastSelectionPrice = (offer, details) => {
  const prices = [serviceSelectionPrice(offer, details)];
  const childCount = Number(details.childBreakfastCount || 0);
  if (childCount > 0) {
    if (!offer.childBreakfastOffer) return null;
    prices.push(serviceSelectionPrice(offer.childBreakfastOffer, { count: childCount, wholeStay: true }));
  }
  return sumMoney(prices);
};

export const serviceBasketSummary = (basket, removals, payment) => {
  const entries = Object.values(basket).map(({ offer, details }) => ({
    offer, details, price: serviceSelectionPrice(offer, details),
  }));
  const changes = [
    ...entries.map((entry) => entry.price),
    ...Object.values(removals).map((item) => {
      const money = asMoney(item.totalAmount || item.total_amount);
      return money ? { ...money, amount: -money.amount } : null;
    }),
  ];
  const balance = asMoney(payment);
  return {
    entries,
    extras: entries.length ? sumMoney(entries.map((entry) => entry.price)) : null,
    total: balance ? sumMoney([balance, ...changes]) : null,
  };
};
